import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/content";

interface NewsCardProps {
  item: NewsItem;
  variant?: "default" | "compact" | "wide";
}

function Badges({ item }: { item: NewsItem }) {
  return (
    <>
      {item.isFresh && (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
          <span className="relative w-1.5 h-1.5 rounded-full bg-background-50 live-dot" />
          刚刚发生
        </span>
      )}
      {item.isLive && (
        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-900">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-radar-line" aria-hidden="true"></i>
          </span>
          实时
        </span>
      )}
      {item.isEvening && (
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-moon-clear-line" aria-hidden="true"></i>
          </span>
          晚间热搜
        </span>
      )}
    </>
  );
}

export default function NewsCard({ item, variant = "default" }: NewsCardProps) {
  if (variant === "compact") {
    return (
      <article className="group flex gap-3 py-4 border-b border-background-300/60 last:border-b-0">
        <span className="w-6 shrink-0 pt-0.5 text-center font-heading text-base font-bold text-background-500 group-hover:text-primary-500 transition-colors">
          <i className="ri-fire-line" aria-hidden="true"></i>
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-medium leading-6 text-foreground-900">
            <Link to={`/article/${item.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {item.title}
            </Link>
          </h3>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="text-primary-600">{item.category}</span>
            <span>{item.time}</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-fire-line" aria-hidden="true"></i>
              </span>
              {item.views}
            </span>
          </p>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 sm:grid-cols-[260px_1fr] gap-4 md:gap-6 rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-5 hover:border-primary-300 transition-colors">
        <div className="w-full h-44 sm:h-40 rounded-md overflow-hidden bg-background-200">
          <img
            src={item.image}
            alt={`${item.title} 配图`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-background-200 px-2.5 py-1 text-[11px] font-medium text-foreground-700">
              {item.category}
            </span>
            <Badges item={item} />
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-7 text-foreground-950">
            <Link to={`/article/${item.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {item.title}
            </Link>
          </h3>
          <p className="mt-2 text-sm leading-6 text-foreground-600 line-clamp-2">{item.excerpt}</p>
          <p className="mt-auto pt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span>{item.author}</span>
            <span>{item.time}</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-fire-line" aria-hidden="true"></i>
              </span>
              热度 {item.heat}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-eye-line" aria-hidden="true"></i>
              </span>
              {item.views}
            </span>
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col rounded-lg border border-background-300/60 bg-background-100 overflow-hidden hover:border-primary-300 transition-colors">
      <div className="w-full h-44 overflow-hidden bg-background-200">
        <img
          src={item.image}
          alt={`${item.title} 配图`}
          className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
        />
      </div>
      <div className="flex flex-col flex-1 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-background-200 px-2.5 py-1 text-[11px] font-medium text-foreground-700">
            {item.category}
          </span>
          <Badges item={item} />
        </div>
        <h3 className="mt-3 font-heading text-base font-bold leading-6 text-foreground-950">
          <Link to={`/article/${item.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {item.title}
          </Link>
        </h3>
        <p className="mt-2 text-sm leading-6 text-foreground-600 line-clamp-3">{item.excerpt}</p>
        <div className="mt-auto pt-4 flex flex-wrap items-center gap-2">
          {item.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-md bg-secondary-50 px-2 py-1 text-[11px] text-secondary-800">
              #{tag}
            </span>
          ))}
        </div>
        <p className="mt-3 pt-3 border-t border-background-300/60 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
          <span>{item.time}</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-fire-line" aria-hidden="true"></i>
            </span>
            {item.heat}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-chat-1-line" aria-hidden="true"></i>
            </span>
            {item.views}
          </span>
        </p>
      </div>
    </article>
  );
}